import { fetchAuthSession } from 'aws-amplify/auth';
import mixpanel from 'mixpanel-browser';
import jwt_decode from 'jwt-decode';

import { STORAGE_SESSION_KEY } from 'config/constants';

interface DecodedToken {
  'cognito:username': string;
  'custom:userId': string;
  phone_number: string;
  phone_number_verified: boolean;
  email: string;
  email_verified: boolean;
  auth_time: number;
  exp: number;
  iat: number;
}

export default class Session {
  public static saveSession(token: string): void {
    localStorage.setItem(STORAGE_SESSION_KEY, token);

    mixpanel.identify(jwt_decode<DecodedToken>(token)['custom:userId']);
  }

  public static clearSession(): void {
    localStorage.clear();

    mixpanel.reset();
  }

  /**
   * Force-refreshes the Cognito session and persists the fresh id token.
   * Returns the token, or `undefined` when the refresh failed — callers decide
   * how to react (the API interceptor / 401 handling logs the user out once
   * the session is genuinely dead).
   */
  public static async refreshCognitoSession(): Promise<string | undefined> {
    try {
      const { tokens } = await fetchAuthSession({ forceRefresh: true });
      const token = tokens?.idToken?.toString();
      if (token) {
        Session.saveSession(token);
        return token;
      }
    } catch (e) {
      /* tslint:disable:no-empty */
    }
    return undefined;
  }

  public static getSessionToken(): string | null {
    const token = localStorage.getItem(STORAGE_SESSION_KEY);
    if (token === 'undefined' || !token) {
      return null;
    }
    return token;
  }

  public static decodeToken(): DecodedToken | null {
    const token = Session.getSessionToken();
    if (token) return jwt_decode(token);

    return null;
  }

  public static async isSessionActive() {
    try {
      const { tokens } = await fetchAuthSession();
      return Boolean(tokens?.idToken);
    } catch (error) {
      return false;
    }
  }
}
